import Image from '@tiptap/extension-image';
import { mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer } from '@tiptap/react';
import ResizableImage from './ResizableImage';

export interface ImageOptions {
  inline: boolean;
  allowBase64: boolean;
  HTMLAttributes: Record<string, any>;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    customImage: {
      setImageFloat: (float: 'none' | 'left' | 'right') => ReturnType;
      setImageSize: (width: string | number, height?: string | number) => ReturnType;
    };
  }
}

export const CustomImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      float: {
        default: 'none',
        parseHTML: (element) => {
          const float = element.style.float;
          if (float === 'left' || float === 'right') return float;
          return 'none';
        },
      },
      width: {
        default: null,
        parseHTML: (element) => {
          const width = element.getAttribute('width') || element.style.width;
          return width ? width.replace('px', '') : null;
        },
      },
      height: {
        default: null,
        parseHTML: (element) => {
          const height = element.getAttribute('height') || element.style.height;
          return height ? height.replace('px', '') : null;
        },
      },
      display: {
        default: 'inline',
        parseHTML: (element) => {
          if (element.style.display === 'block' && element.style.margin?.includes('auto')) {
            return 'block';
          }
          return 'inline';
        },
      },
      zIndex: {
        default: 'auto',
        parseHTML: (element) => element.style.zIndex || 'auto',
      },
      position: {
        default: 'relative',
        parseHTML: (element) => element.style.position || 'relative',
      },
    };
  },

  addNodeView() {
    return ReactNodeViewRenderer(ResizableImage);
  },

  renderHTML({ node, HTMLAttributes }) {
    const styles: string[] = [];
    
    // Handle position and z-index for layering
    if (HTMLAttributes.position && HTMLAttributes.position !== 'static') {
      styles.push(`position: ${HTMLAttributes.position}`);
    }
    if (HTMLAttributes.zIndex && HTMLAttributes.zIndex !== 'auto') {
      styles.push(`z-index: ${HTMLAttributes.zIndex}`);
    }
    
    // Handle float styling
    if (HTMLAttributes.float && HTMLAttributes.float !== 'none') {
      const margin = HTMLAttributes.float === 'left' ? '0 16px 8px 0' : '0 0 8px 16px';
      styles.push(`float: ${HTMLAttributes.float}`, `margin: ${margin}`);
    }
    
    // Handle centered/block display
    if (HTMLAttributes.display === 'block') {
      styles.push('display: block', 'margin-left: auto', 'margin-right: auto');
    }
    
    // Handle explicit width
    if (HTMLAttributes.width) {
      styles.push(`width: ${HTMLAttributes.width}px`);
    }
    
    // Handle explicit height
    if (HTMLAttributes.height) {
      styles.push(`height: ${HTMLAttributes.height}px`);
    }

    // Remove custom attributes from HTMLAttributes to avoid duplicate attributes
    const { float, display, width, height, zIndex, position, ...rest } = HTMLAttributes;

    return [
      'img',
      mergeAttributes(this.options.HTMLAttributes, rest, {
        style: styles.length ? styles.join('; ') : undefined,
        width: width || undefined,
        height: height || undefined,
      }),
    ];
  },

  addCommands() {
    return {
      ...this.parent?.(),
      setImageFloat:
        (float: 'none' | 'left' | 'right') =>
        ({ commands }) => {
          return commands.updateAttributes('image', { 
            float,
            display: 'inline',
          });
        },
      setImageSize:
        (width: string | number, height?: string | number) =>
        ({ commands }) => {
          return commands.updateAttributes('image', { width, height });
        },
    };
  },
});

export default CustomImage;
